import { useOptionalDependency } from 'ditox-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LNGS_TOKEN } from '@/container';
import AppCell from '../common/app-cell';
import ScrollSelector from '../common/ScrollSelector';
import PageLayout from '../layouts/page-layout/page-layout';

type LanguageSelectProps = {
  show: boolean;
  onClose: () => void;
};

const LanguageSelect = ({ show, onClose }: LanguageSelectProps) => {
  const supportedLanguages = useOptionalDependency(SUPPORTED_LNGS_TOKEN);
  const { t, i18n } = useTranslation();
  const [isLanguageSwitching, setLanguageSwitching] = useState(false);

  const handleLanguageChange = async (lang: string) => {
    setLanguageSwitching(true);

    await i18n.changeLanguage(lang);

    if (i18n.language === lang) {
      setLanguageSwitching(false);
    }

    onClose();
  };

  return (
    <>
      <ScrollSelector
        show={show}
        onCancel={onClose}
        onConfirm={handleLanguageChange}
        title={t('general-settings.language')}
        defaultValue={i18n.resolvedLanguage}
        items={
          supportedLanguages?.map(({ value, label }) => ({
            key: value,
            label,
            value,
          })) ?? []
        }
      />

      {isLanguageSwitching && (
        <div className='absolute inset-0 z-30 flex items-center justify-center bg-black/70 text-center backdrop-blur-[0.625rem]'>
          <span className='text-base font-normal text-neutral-0'>{t('general-settings.language-switching')}</span>
        </div>
      )}
    </>
  );
};
export default LanguageSelect;

export const LanguageSelectPage = ({ onBack }: { onBack?: () => void }) => {
  const supportedLanguages = useOptionalDependency(SUPPORTED_LNGS_TOKEN);
  const { t, i18n } = useTranslation();

  const handleLanguageChange = async (lang: string) => {
    await i18n.changeLanguage(lang);
  };
  return (
    <PageLayout
      title={t('general-settings.language')}
      headerConfig={{
        navigateBack: () => {
          onBack?.();
        },
      }}>
      {supportedLanguages?.map(({ value, label }) => (
        <AppCell
          key={value}
          label={label}
          hoverable
          suffix={
            <input
              type='radio'
              name='language'
              value={value}
              defaultChecked={i18n.resolvedLanguage === value}
              onChange={(e) => {
                const value = e.target.value;
                handleLanguageChange(value);
              }}
              className='radio-primary radio mr-2 ms-2 rounded-full border-base-200'
            />
          }
        />
      ))}
    </PageLayout>
  );
};
